// src/services/crosswordService.js
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, orderBy, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { isToday, isPast } from '../utils/dateUtils';
import { syncIconCluesFromPuzzle } from './iconCluesService';

export const PUZZLES_COLLECTION = 'crossword-puzzles';

/**
 * Fetches all crossword puzzles from Firestore
 * @returns {Promise<Array>} Array of puzzle objects with auto-set published status
 */
export const fetchCrosswordPuzzles = async () => {
  try {
    const q = query(collection(db, PUZZLES_COLLECTION), orderBy('id'));
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      // Auto-set published status based on date
      data.published = isToday(data.id) || isPast(data.id);
      return data;
    });
    return list;
  } catch (error) {
    console.error('Failed to fetch puzzles:', error);
    throw error;
  }
};

/**
 * Fetches a single crossword puzzle by ID
 * @param {string} puzzleId - Puzzle ID (date string DD-MM-YYYY)
 * @returns {Promise<Object|null>} Puzzle object or null if not found
 */
export const fetchCrosswordPuzzle = async (puzzleId) => {
  try {
    const puzzleDoc = await getDoc(doc(db, PUZZLES_COLLECTION, puzzleId));
    if (puzzleDoc.exists()) {
      const freshPuzzle = { id: puzzleDoc.id, ...puzzleDoc.data() };
      freshPuzzle.published = isToday(freshPuzzle.id) || isPast(freshPuzzle.id);
      return freshPuzzle;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch puzzle:', error);
    throw error;
  }
};

/**
 * Creates a new empty crossword puzzle
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {Object} New puzzle object
 */
export const createCrosswordPuzzle = (dateStr) => {
  return {
    id: dateStr,
    gridSize: { width: 6, height: 7 },
    words: [],
    blackSquares: [],
    published: isToday(dateStr) || isPast(dateStr),
    ready: false,
    isToday: isToday(dateStr)
  };
};

/**
 * Saves a crossword puzzle to Firestore and updates the manifest
 * @param {Object} puzzleData - Puzzle object to save
 * @returns {Promise<void>}
 */
export const saveCrosswordPuzzle = async (puzzleData) => {
  try {
    // Check if puzzle is ready (all words have clues)
    puzzleData.ready = puzzleData.words.length > 0 &&
                      puzzleData.words.every(word => word.clue.trim() !== '');

    // Save puzzle document
    await setDoc(doc(db, PUZZLES_COLLECTION, puzzleData.id), puzzleData);

    // Update manifest with new version
    const manifestRef = doc(db, 'metadata', 'crosswordPuzzles');
    const manifestDoc = await getDoc(manifestRef);
    const currentManifest = manifestDoc.exists() ? (manifestDoc.data().manifest || {}) : {};

    await setDoc(manifestRef, {
      manifest: {
        ...currentManifest,
        [puzzleData.id]: puzzleData.contentVersion || null
      },
      lastUpdated: serverTimestamp()
    }, { merge: true });

    // Sync icon-word associations from this puzzle's clues (fire-and-forget)
    syncIconCluesFromPuzzle(puzzleData.words).catch(err =>
      console.error('Failed to sync icon clues:', err)
    );

    console.log('Puzzle saved successfully');
  } catch (error) {
    console.error('Failed to save puzzle:', error);
    throw error;
  }
};

/**
 * Deletes a crossword puzzle from Firestore and removes it from the manifest
 * @param {string} puzzleId - Puzzle ID to delete
 * @returns {Promise<void>}
 */
export const deleteCrosswordPuzzle = async (puzzleId) => {
  try {
    await deleteDoc(doc(db, PUZZLES_COLLECTION, puzzleId));

    // Remove from manifest
    const manifestRef = doc(db, 'metadata', 'crosswordPuzzles');
    const manifestDoc = await getDoc(manifestRef);
    if (manifestDoc.exists()) {
      const currentManifest = manifestDoc.data().manifest || {};
      delete currentManifest[puzzleId];

      await setDoc(manifestRef, {
        manifest: currentManifest,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    }

    console.log('Puzzle deleted successfully');
  } catch (error) {
    console.error('Failed to delete puzzle:', error);
    throw error;
  }
};
